import { useEffect, useState } from 'react';
import { Button, Card, Col, Form, Row, Tab, Tabs } from 'react-bootstrap';
import PageHeader from 'shared/page-header';
import Layout from 'wrappers/layout';
import PhysicalOrders from './components/PhysicalOrders';
import DeliveryOrdersServices from './services/delivery.order.service';
import { Pagination } from 'shared/common/pagination';
import { userStore } from 'store/UserStore';
import { saveAs } from 'file-saver';
import { toast } from 'react-toastify';
import Loader from 'shared/common/loader';

interface DeliveryOrderFilters {
  metalCode?: string;
  fromDate?: string;
  toDate?: string;
}

interface PropsType {}

const DeliveryOrders: React.FC<PropsType> = () => {
  const [selectedTab, setSelectedTab] = useState<string>('deliveryOrders');
  const [deliveryOrders, setDeliveryOrders] = useState<any[]>([]);
  const [loadingOrders, setLoadingOrders] = useState<boolean>(false);
  const [downloadingInvoice, setDownloadingInvoice] = useState<boolean>(false);
  const [metaData, setMetaData] = useState<{ current: number; pages: number }>({
    current: 0,
    pages: 0,
  });
  const [currentPage, setCurrentPage] = useState<number>(0);
  const [filter, setFilter] = useState<DeliveryOrderFilters>({
    metalCode: '',
    fromDate: '',
    toDate: '',
  });

  const [user] = userStore((state: any) => [state.user]);

  // Tab change
  const onChangeTab = (tab: string | null) => {
    if (tab) setSelectedTab(tab);
  };

  // Filter change
  const handleChangeFilter = (e: React.ChangeEvent<any>) => {
    const { name, value } = e.target;
    setFilter((prev) => ({ ...prev, [name]: value }));
  };

  const getDeliveryOrders = async (
    page: number = currentPage,
    filtersParam: DeliveryOrderFilters = filter
  ) => {
    setLoadingOrders(true);
    try {
      const res = await DeliveryOrdersServices.getDeliverOrders(
        filtersParam,
        page
      );

      setCurrentPage(page);
      setMetaData({
        current: res.data.meta.page,
        pages: res.data.meta.totalPages + 1,
      });

      setDeliveryOrders(res?.data?.data?.result || []);
    } catch (error) {
      console.error('Failed to fetch delivery orders:', error);
      setDeliveryOrders([]);
    } finally {
      setLoadingOrders(false);
    }
  };

  const applyFilters = () => {
    setCurrentPage(currentPage);
    getDeliveryOrders(currentPage);
  };

  const resetFilters = () => {
    const clearedFilters = { metalCode: '', fromDate: '', toDate: '' };
    setFilter(clearedFilters);
    setCurrentPage(0);
    getDeliveryOrders(0, clearedFilters);
  };

  const downloadDeliveryInvoice = async (invoiceRef: any) => {
    if (!invoiceRef) {
      toast.error('Invoice reference not found');
      return;
    }

    setDownloadingInvoice(true);
    try {
      const res = await DeliveryOrdersServices.downloadDeliveryInvoice(
        invoiceRef,
        user?.userId
      );

      if (res.status === 200 && res.data?.data?.fileUrl) {
        const fileUrl = res.data.data.fileUrl;
        window.open(fileUrl, '_blank');
        
      } else {
        toast.error('Failed to download invoice');
      }
    } catch (error: any) {
      console.error('Download invoice error:', error);
      if (error?.response?.data?.errors?.length > 0) {
        toast.error(error.response.data.errors[0].title);
      } else {
        toast.error('Something went wrong while downloading invoice');
      }
    } finally {
      setDownloadingInvoice(false);
    }
  };

  useEffect(() => {
    document.title = 'Delivery Orders - eBullion';
    getDeliveryOrders(0);
  }, []);

  return (
    <Layout>
      {downloadingInvoice && <Loader/>}
      <div className='pb'>
        <PageHeader
          mainTitle='Delivery Orders'
          subTitle='View and track all your physical delivery orders'
          isShowSub={true}
        />

        <Row>
          <Col md={12}>
            <Tabs
              activeKey={selectedTab}
              id='delivery-orders-tab'
              className='mb-3'
              onSelect={onChangeTab}
            >
              <Tab eventKey='deliveryOrders' title='Delivery Orders'>
                <div>
                  <h6>Order History</h6>

                  <Card>
                    <Card.Body>
                      <Row className='align-items-center'>
                        <Col md={3}>
                          <Form.Group className='mb-3' controlId='orderMetal'>
                            <Form.Label>Order Metal</Form.Label>
                            <Form.Select
                              name='metalCode'
                              onChange={handleChangeFilter}
                              value={filter.metalCode}
                            >
                              <option value=''>All metals</option>
                              <option value='Ag'>Silver (Ag)</option>
                              <option value='Au'>Gold (Au)</option>
                            </Form.Select>
                          </Form.Group>
                        </Col>

                        <Col md={2}>
                          <Form.Group
                            className='mb-3'
                            controlId='orderFromDate'
                          >
                            <Form.Label>Order From Date</Form.Label>
                            <Form.Control
                              type='date'
                              name='fromDate'
                              onChange={handleChangeFilter}
                              value={filter.fromDate}
                            />
                          </Form.Group>
                        </Col>

                        <Col md={2}>
                          <Form.Group className='mb-3' controlId='orderToDate'>
                            <Form.Label>Order To Date</Form.Label>
                            <Form.Control
                              type='date'
                              name='toDate'
                              onChange={handleChangeFilter}
                              value={filter.toDate}
                              min={filter.fromDate}
                              disabled={!filter.fromDate}
                            />
                          </Form.Group>
                        </Col>

                        <Col md={2} className='p-0 pt-2'>
                          <Button
                            className='mx-2'
                            variant='primary'
                            onClick={applyFilters}
                            disabled={loadingOrders}
                          >
                            {loadingOrders ? 'Loading...' : 'Apply'}
                          </Button>
                          <Button
                            variant='secondary'
                            onClick={resetFilters}
                            disabled={loadingOrders}
                          >
                            Reset
                          </Button>
                        </Col>
                      </Row>
                    </Card.Body>
                  </Card>

                  <div className='t-wrap overflow-auto mt-3'>
                    <PhysicalOrders
                      orders={deliveryOrders}
                      downloadDeliveryInvoice={downloadDeliveryInvoice}
                    />
                  </div>

                  {/* Pagination */}
                  <Row>
                    <Col md={12}>
                      <Pagination
                        meta={metaData}
                        handlePageChange={(page: number) =>
                          getDeliveryOrders(page)
                        }
                      />
                    </Col>
                  </Row>
                </div>
              </Tab>
            </Tabs>
          </Col>
        </Row>
      </div>
    </Layout>
  );
};

export default DeliveryOrders;
